/**
 * App orchestrator. Owns mode transitions (idle -> preview -> navigating),
 * wires GPS fixes into the navigator and camera, and connects the UI panels.
 */

import './style.css';
import { registerSW } from 'virtual:pwa-register';

import { APP_VERSION, hasMapbox } from './core/config';
import { getLang, t } from './core/i18n';
import { store, select } from './core/store';
import { activeVehicle } from './core/settings';
import type { Fix, LngLat, NavProgress, Place, Route, Trip } from './core/types';
import { cumulativeDistances, distance } from './core/geo';
import { formatClock, formatDateTime, formatDistanceShort, routeLetter, setClock24 } from './core/format';
import { addRecent, listTrips, saveTrip } from './core/db';

import { GpsWatcher } from './nav/gps';
import { fetchRouteAlternatives, fetchRoutes } from './nav/directions';
import { RouteNavigator } from './nav/navigator';
import { TripRecorder } from './nav/tripRecorder';
import { computeRouteStats, statsFor, type RouteStats } from './nav/routeStats';
import { applyPace, paceDuration, tierForStep } from './nav/paceModel';
import { planFerry } from './nav/ferry';
import { adviseOnTraffic, pickLowTraffic, sampleRemaining } from './nav/trafficWatch';
import { roadWidthProfile, type RoadWidthProfile } from './nav/bigRoads';
import { roadLabel } from './nav/roads';
import { learnedVias } from './nav/learnedRoutes';
import {
  buildRoughIndex,
  roughnessOfRoute,
  roughAhead,
  roughPenaltySeconds,
  type RoughIndex,
  type RouteRoughness,
} from './nav/roughMap';
import { RoughnessMeter } from './nav/roughness';
import { VehicleHealthMonitor, assessHealthDrift } from './nav/vehicleHealth';
import { BreadcrumbTrail } from './nav/breadcrumbs';
import { readPoint } from './nav/tripRecorder';
import { enableWakeLock, disableWakeLock } from './nav/wakelock';
import { reverseGeocode, reverseLocality } from './search/geocode';
import { adviceKey, fetchAdvice, type RouteAdvice } from './ai/advisor';
import { Fleet, type MessageKind, type QuickMessage } from './fleet/fleet';
import { findFuelStopsAhead, etaToStop, pttGapStats, type FuelStop, type PttGapStats } from './nav/fuelStops';
import { LaneGuideCache } from './ai/laneAdvisor';
import { scanRouteClearances, type RouteClearance } from './nav/clearance';
import { analyseElevation, type ElevationProfile } from './nav/elevation';

import { MapView } from './map/mapView';
import { ManeuverCard } from './ui/maneuverCard';
import { NavHud } from './ui/navHud';
import { SearchPanel } from './ui/searchPanel';
import { RoutePreview } from './ui/routePreview';
import { SettingsPanel } from './ui/settingsPanel';
import { mirrorLangForWorker } from './pwa/push';
import { TripsPanel } from './ui/tripsPanel';
import { SpotifyBar } from './ui/spotifyBar';
import { FleetBubble } from './ui/fleetBubble';
import { FuelBubble, type FuelChoice } from './ui/fuelBubble';
import { VehiclePicker } from './ui/vehiclePicker';
import { RouteInfoSheet } from './ui/routeInfo';
import { LiveDataPanel } from './ui/liveData';

import { announce, primeSpeech, setDucker, stopSpeaking } from './audio/voice';
import { SpotifyPlayer, completeLoginIfReturning, isAuthed } from './audio/spotify';

import { LoginView } from './auth/loginView';
import * as authApi from './auth/api';
import type { SavedPlace, User } from './auth/api';

registerSW({ immediate: true });

const appEl = document.getElementById('app')!;
const uiEl = document.getElementById('ui')!;
const mapEl = document.getElementById('map')!;

/**
 * Startup must never fail silently to a black screen. Any uncaught error
 * before the UI exists gets rendered full-screen so the failure is readable
 * on a phone or head unit with no devtools.
 */
function fatalScreen(title: string, detail: string): void {
  uiEl.innerHTML = `<div class="fatal"><h1>${title}</h1><p>${detail}</p>
    <button class="btn btn--primary" onclick="location.reload()">Reload</button></div>`;
}

let bootFinished = false;
let bootStartedAt = 0;

/**
 * Startup breadcrumbs, made impossible to miss: every step is mirrored into
 * the tab title and a small on-screen strip. On a healthy boot the strip
 * exists for under a second; on a stall it stays, telling us the exact step.
 */
const bootTrace: string[] = [];
let traceEl: HTMLElement | null = null;

function trace(step: string): void {
  bootTrace.push(step);
  document.title = `WF·${step}`;
  if (!traceEl) {
    traceEl = document.createElement('div');
    traceEl.className = 'boot-trace';
    document.body.appendChild(traceEl);
  }
  traceEl.textContent = bootTrace.join(' → ');
}

function traceDone(): void {
  bootFinished = true;
  document.title = 'Wayfarer Navigation';
  traceEl?.remove();
  traceEl = null;
}

function traceText(): string {
  return `Progress: ${bootTrace.join(' → ') || '(nothing ran)'}`;
}

// Watch continuously: once boot() has started, it must finish or error within
// 15 s - otherwise show the stall screen with the breadcrumbs.
window.setInterval(() => {
  if (!bootFinished && bootStartedAt && Date.now() - bootStartedAt > 15_000 && !document.querySelector('.fatal')) {
    fatalScreen('Startup stalled', traceText());
  }
}, 5_000);

/**
 * Keep every device on the latest deploy.
 *
 * This used to set a one-shot `wf-sw-reloaded` flag: the app reloaded for the
 * FIRST service-worker takeover of a tab session and never again. A phone
 * running this as a PWA keeps the same session alive for days, so the second
 * and every later deploy was fetched and then ignored - the driver kept
 * running an old bundle and reported bugs that had already been fixed.
 *
 * The flag is now a timestamp, which still prevents a reload loop (two
 * takeovers seconds apart) but lets every genuine update through. And because
 * a long-lived PWA may never navigate, we actively poll for a new worker on
 * resume and on a timer, rather than waiting for one to appear by luck.
 */
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const last = Number(sessionStorage.getItem('wf-sw-reloaded') ?? 0);
    if (Date.now() - last > 10_000) {
      sessionStorage.setItem('wf-sw-reloaded', String(Date.now()));
      location.reload();
    }
  });

  void navigator.serviceWorker.ready.then((reg) => {
    const check = (): void => void reg.update().catch(() => {});
    // Never mid-drive: a reload during navigation would drop the route.
    const checkWhenIdle = (): void => {
      if (store.get().mode === 'idle') check();
    };
    setInterval(checkWhenIdle, 15 * 60_000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) checkWhenIdle();
    });
    check();
  });
}

window.addEventListener('error', (e) => {
  if (!bootFinished) {
    fatalScreen(
      'Wayfarer could not start',
      `${String(e.error?.message ?? e.message)}<br/><small>${traceText()}</small>`,
    );
  }
});
window.addEventListener('unhandledrejection', (e) => {
  if (!bootFinished) {
    fatalScreen(
      'Wayfarer could not start',
      `${String((e.reason as Error)?.message ?? e.reason)}<br/><small>${traceText()}</small>`,
    );
  }
});

/** Mapbox GL v3 needs WebGL2; without it (hardware acceleration off, old GPU
 *  driver, some head units) the Map constructor throws. Check up front so the
 *  driver gets an actionable message instead of a black screen. */
function webgl2Available(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return canvas.getContext('webgl2') !== null;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------------- */
/* Auth gate                                                                  */
/* ------------------------------------------------------------------------- */

let currentUser: User | null = null;

async function gate(): Promise<void> {
  trace('gate');
  if (!hasMapbox()) {
    uiEl.innerHTML = `<div class="fatal">
      <h1>Wayfarer</h1>
      <p>No Mapbox token configured. Copy <code>.env.example</code> to <code>.env.local</code>,
      paste your free token from account.mapbox.com, and rebuild.</p></div>`;
    return;
  }
  try {
    const { user } = await authApi.me();
    trace(user ? `me:${user.status}` : 'me:none');
    if (user && user.status === 'approved') {
      currentUser = user;
      void boot();
      return;
    }
  } catch {
    /* no backend reachable - fall through to login, which will show the error */
  }
  trace('login:shown');
  const login = new LoginView(uiEl, (user) => {
    trace('login:authed');
    currentUser = user;
    login.destroy();
    void boot();
  });
}

/* ------------------------------------------------------------------------- */
/* Main app                                                                   */
/* ------------------------------------------------------------------------- */

async function boot(): Promise<void> {
  bootStartedAt = Date.now();
  trace('boot');
  if (!webgl2Available()) {
    fatalScreen(
      'Map engine unavailable',
      'This browser has no WebGL2, which the map needs. Usually this means ' +
        'hardware acceleration is turned OFF: open Chrome Settings → System → ' +
        'enable "Use graphics acceleration when available", relaunch the browser, ' +
        'and reload this page. (chrome://gpu shows the current state.)',
    );
    return;
  }

  /* ------------------------------ theme ------------------------------- */
  const resolveTheme = (): 'night' | 'day' => {
    const pref = store.get().settings.theme;
    if (pref !== 'auto') return pref;
    const h = new Date().getHours() + new Date().getMinutes() / 60;
    return h >= 6.25 && h < 18.5 ? 'day' : 'night';
  };

  /* ------------------------------- map -------------------------------- */
  trace('webgl:ok');
  const { loadStrippedStyle } = await import('./map/styleLoader');
  trace('style:fetch');
  // Thai UI gets a Thai map: the style is rewritten to prefer each place's own
  // name over the English one. See map/styleLoader.ts - there is no name_th
  // field to switch to, so this works by no longer overriding `name`.
  const labelLang: 'en' | 'local' = getLang() === 'th' ? 'local' : 'en';
  const bootStyle = await loadStrippedStyle(resolveTheme(), labelLang);
  trace(typeof bootStyle === 'string' ? 'style:fallback' : 'style:ok');
  const mapView = new MapView(mapEl, bootStyle, {
    onLongPress: (point) => void pickDroppedPin(point),
    onAltRouteClick: (routeId) => {
      const idx = store.get().routes.findIndex((r) => r._id === routeId);
      if (idx >= 0) selectRoute(idx);
    },
    onUserGesture: () => {
      // Breaking follow does not by itself warrant the button - it appears
      // once the vehicle has actually drifted off screen.
      refreshRecenter();
    },
    onPlaceClick: (marker) => {
      if (store.get().mode === 'navigating') return;
      void pickDestination({
        id: `saved:${marker.id}`,
        name: marker.name,
        address: `Saved by ${marker.creatorName}`,
        center: [marker.lng, marker.lat],
        kind: 'saved',
      });
    },
  });  mapView.setLabelLanguage(labelLang);
  mapView.setBuildings3d(store.get().settings.buildings3d);
  select(
    (s) => s.settings.buildings3d,
    (on) => mapView.setBuildings3d(on),
    { immediate: false },
  );
  const applyCameraPrefs = () => {
    const s = store.get().settings;
    mapView.setCameraPrefs({ pitch: s.cameraPitch, driveZoom: s.driveZoom });
  };
  applyCameraPrefs();
  select((s) => s.settings.cameraPitch, applyCameraPrefs, { immediate: false });
  select((s) => s.settings.driveZoom, applyCameraPrefs, { immediate: false });


  trace('map:ok');
  /* -------------------------- shared saved places ----------------------- */
  let sharedPlaces: SavedPlace[] = [];

  async function loadPlaces(): Promise<void> {
    try {
      sharedPlaces = (await authApi.fetchPlaces()).places;
      mapView.setPlaces(sharedPlaces);
    } catch {
      /* offline or backend down - markers just don't refresh */
    }
  }
  void loadPlaces();
  // Shared locations from other drivers should appear without a reload.
  setInterval(() => void loadPlaces(), 90_000);

  function isDestSaved(): boolean {
    const dest = store.get().destination;
    if (!dest) return false;
    return sharedPlaces.some((p) => distance([p.lng, p.lat], dest.center) < 30);
  }

  let lastTheme = resolveTheme();
  setInterval(() => {
    const t = resolveTheme();
    if (t !== lastTheme) {
      lastTheme = t;
      void mapView.setTheme(t);
    }
  }, 60_000);
  select(
    (s) => s.settings.theme,
    () => {
      const t = resolveTheme();
      if (t !== lastTheme) {
        lastTheme = t;
        void mapView.setTheme(t);
      }
    },
    { immediate: false },
  );

  /* ------------------------------ Spotify ------------------------------ */
  const spotifyPlayer = new SpotifyPlayer({
    onState: (s) => spotifyBar.update(s),
  });
  setDucker(spotifyPlayer);
  void completeLoginIfReturning().then((returned) => {
    if (returned || isAuthed()) void spotifyPlayer.init();
  });
  // Swap the media bar between Spotify and Audible the moment the setting flips.
  select(
    (s) => s.settings.audioSource,
    () => spotifyBar.refresh(),
    { immediate: false },
  );

  trace('spotify:ok');
  /* -------------------------------- UI --------------------------------- */
  const maneuverCard = new ManeuverCard(uiEl);

  /*
   * Keep the right-hand control rail below the sign.
   *
   * The card grows and shrinks - lane chips, a lane-trap warning, a second
   * road on the strip - so any fixed offset for the buttons beside it is wrong
   * some of the time. Measuring it is the only version that cannot overlap.
   */
  function measureRail(): void {
    // In landscape the card is beside the rail rather than above it, so the
    // rail starts at the top. An inline style beats the stylesheet, so this
    // has to zero it out itself rather than leaving it to the media query.
    const landscape = window.innerWidth > window.innerHeight && window.innerHeight < 620;
    const h =
      landscape || maneuverCard.el.hidden ? 0 : maneuverCard.el.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--card-h', `${Math.round(h)}px`);
  }
  new ResizeObserver(measureRail).observe(maneuverCard.el);
  window.addEventListener('resize', measureRail);
  window.addEventListener('orientationchange', () => setTimeout(measureRail, 250));
  measureRail();

  const hud = new NavHud(uiEl, {
    onEnd: () => endNavigation('ended'),
    onRecenter: () => {
      // Hand it the position to go to - without one it can only raise the
      // follow flag, which does nothing at all outside navigation.
      const fix = store.get().fix;
      mapView.recenter(fix ? [fix.lng, fix.lat] : null);
      hud.setRecenterVisible(false);
      // Re-check once the ease has landed: if the vehicle is somehow still off
      // screen the button must come back rather than leave the driver stranded.
      window.setTimeout(refreshRecenter, 900);
    },
    onOverview: () => {
      const route = activeRoute();
      if (route) mapView.fitRoute([route], isPortrait(), 0, 0);
      window.setTimeout(refreshRecenter, 800);
    },
  });

  const idleBar = document.createElement('div');
  idleBar.className = 'idlebar';
  idleBar.innerHTML = `
    <div class="idlebar__status" hidden></div>
    <div class="idlebar__buttons">
      <button class="icon-btn idlebar__btn" data-act="trips" aria-label="Trips">
        <svg viewBox="0 0 24 24"><path d="M4 19 C4 12 10 12 12 8 C13 6 12 4 12 4 M12 4 L9 6 M12 4 L15 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="5" cy="19" r="2" fill="currentColor"/><circle cx="19" cy="5" r="2" fill="currentColor"/></svg>
      </button>
      <button class="icon-btn idlebar__btn" data-act="share" aria-label="Share my location">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3.2" fill="currentColor"/><path d="M4.5 12 a7.5 7.5 0 0 1 15 0 M7.5 12 a4.5 4.5 0 0 1 9 0" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>
      </button>
      <button class="icon-btn idlebar__btn" data-act="settings" aria-label="Settings">
        <svg viewBox="0 0 24 24"><path d="M12 8.5 a3.5 3.5 0 1 0 0 7 a3.5 3.5 0 0 0 0-7 Z M19 12 c0-.6.5-1.2.4-1.8 l-1.8-.6 -.5-1.2 .9-1.7 c-.7-.8-1.2-1.2-2-1.7 l-1.6 1 -1.3-.5 -.7-1.8 h-2.4 l-.7 1.8 -1.3.5 -1.6-1 c-.8.5-1.3 1-2 1.7 l.9 1.7 -.5 1.2 -1.8.6 c-.1.6.4 1.2.4 1.8 s-.5 1.2-.4 1.8 l1.8.6 .5 1.2 -.9 1.7 c.7.8 1.2 1.2 2 1.7 l1.6-1 1.3.5 .7 1.8 h2.4 l.7-1.8 1.3-.5 1.6 1 c.8-.5 1.3-1 2-1.7 l-.9-1.7 .5-1.2 1.8-.6 c.1-.6-.4-1.2-.4-1.8 Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>
      </button>
    </div>
    <button class="fab" data-act="search" aria-label="Search destination">
      <svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="2.4"/><path d="M15.5 15.5 L21 21" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>
      <span>${t('idle.whereTo')}</span>
    </button>`;
  uiEl.appendChild(idleBar);

  /**
   * Trips are not always planned from where you are standing - the driver may
   * be costing a run that starts at the depot tomorrow morning. When
   * `plannedOrigin` is set the preview routes from there instead of the GPS
   * fix; pressing Start still re-routes from the real position, because
   * turn-by-turn has to begin where the vehicle actually is.
   */
  let plannedOrigin: Place | null = null;
  let searchTarget: 'origin' | 'destination' = 'destination';

  async function setPlannedOrigin(place: Place | null): Promise<void> {
    plannedOrigin = place;
    searchTarget = 'destination';
    setPanel(null);
    await refetchPreviewRoutes();
  }

  const searchPanel = new SearchPanel(
    uiEl,
    () => currentFixLngLat(),
    (place) => {
      if (searchTarget === 'origin') void setPlannedOrigin(place);
      else void pickDestination(place);
    },
    () => setPanel(null),
    () => sharedPlaces,
    async (id) => {
      await authApi.deletePlace(id);
      await loadPlaces();
    },
    async (place) => {
      const name = await openNameDialog(t('place.rename'), place.name);
      if (name == null || name === place.name) return;
      try {
        await authApi.renamePlace(place.id, name);
      } catch (err) {
        alert(err instanceof Error ? err.message : t('place.saveFailed'));
        return;
      }
      await loadPlaces();
    },
    async () => {
      await loadPlaces();
    },
  );

  const preview = new RoutePreview(uiEl, {
    onGo: startNavigation,
    onCancel: cancelPreview,
    onSelectRoute: selectRoute,
    onSwitchVehicle: () => {
      // Was a blind cycle to the next vehicle, which never answered "which one
      // am I routing as right now". Now it opens the list with the active one
      // ticked - see ui/vehiclePicker.ts.
      const s = store.get().settings;
      vehiclePicker.open(s.vehicles, s.activeVehicleId);
    },
    onSavePlace: () => {
      const dest = store.get().destination;
      if (!dest) return;
      openSaveDialog(dest.name, dest.center);
    },
    onAvoidTraffic: () => {
      const s = store.get();
      const pick = pickLowTraffic(s.routes, routeStats);
      if (pick === null) return;
      if (pick === s.selectedRouteIndex) {
        statusFlash(t('traffic.already'));
        return;
      }
      selectRoute(pick);
      statusFlash(t('traffic.picked', { letter: routeLetter(pick) }));
    },
    onPickOrigin: () => {
      searchTarget = 'origin';
      setPanel('search');
    },
    onClearOrigin: () => void setPlannedOrigin(null),
    onDismissAdvice: () => {
      advice = null;
      adviceDismissed = true;
      renderPreview();
    },
  });

  /* --------------------------- save place dialog ------------------------ */

  /**
   * Minimal name prompt, styled like the save dialog. Used when sharing the
   * current position - "add a popup to save the name of the place, instead of
   * editing later" - and when renaming an existing saved place. Resolves with
   * the chosen name, or null on cancel.
   */
  function openNameDialog(title: string, prefill: string): Promise<string | null> {
    return new Promise((resolve) => {
      const dialog = document.createElement('div');
      dialog.className = 'save-dialog';
      dialog.innerHTML = `
        <div class="save-dialog__card">
          <h3>${escapeText(title)}</h3>
          <input class="save-dialog__name" type="text" maxlength="120"
                 placeholder="${t('save.name.ph')}" value="${prefill.replace(/"/g, '&quot;')}"/>
          <div class="save-dialog__actions">
            <button class="btn btn--ghost" data-act="cancel">${t('preview.cancel')}</button>
            <button class="btn btn--primary" data-act="save">${t('save.save')}</button>
          </div>
        </div>`;
      uiEl.appendChild(dialog);

      const input = dialog.querySelector<HTMLInputElement>('.save-dialog__name')!;
      // Select the prefill so typing replaces it in one motion.
      setTimeout(() => {
        input.focus();
        input.select();
      }, 60);

      const finish = (value: string | null) => {
        dialog.remove();
        resolve(value);
      };
      dialog.querySelector('[data-act=cancel]')!.addEventListener('click', () => finish(null));
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) finish(null);
      });
      const save = () => finish(input.value.trim() || prefill);
      dialog.querySelector('[data-act=save]')!.addEventListener('click', save);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') save();
      });
    });
  }
  /** Folder names are user text - escape before they reach markup. */
  function escapeAttr(s: string): string {
    return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  function openSaveDialog(prefill: string, center: LngLat): void {
    const categories: Array<{ id: authApi.PlaceCategory; label: string }> = [
      { id: 'home', label: t('cat.home') },
      { id: 'work', label: t('cat.work') },
      { id: 'showroom', label: t('cat.showroom') },
      { id: 'depot', label: t('cat.depot') },
      { id: 'other', label: t('cat.other') },
    ];
    let selected: authApi.PlaceCategory = 'other';
    /*
     * File it at the moment of saving.
     *
     * Saving and then hunting the place down in the list to file it is two jobs
     * for one intention - and the moment you know which folder a customer
     * belongs in is the moment you save them. The chooser is populated from the
     * fleet's folders; picking "New folder" names one on the spot, and the
     * server creates it as part of filing.
     */
    let folder: string | null = null;

    const dialog = document.createElement('div');
    dialog.className = 'save-dialog';
    dialog.innerHTML = `
      <div class="save-dialog__card">
        <h3>${t('save.title')}</h3>
        <input class="save-dialog__name" type="text" maxlength="120"
               placeholder="${t('save.name.ph')}" value="${prefill.replace(/"/g, '&quot;')}"/>
        <div class="save-dialog__cats">
          ${categories
            .map(
              (c) =>
                `<button class="chip ${c.id === selected ? 'is-on' : ''}" data-cat="${c.id}">${c.label}</button>`,
            )
            .join('')}
        </div>
        <div class="save-dialog__folder">
          <span class="save-dialog__folder-label">${t('folder.saveInto')}</span>
          <div class="save-dialog__folders"></div>
        </div>
        <div class="save-dialog__actions">
          <button class="btn btn--ghost" data-act="cancel">${t('preview.cancel')}</button>
          <button class="btn btn--primary" data-act="save">${t('save.save')}</button>
        </div>
      </div>`;
    uiEl.appendChild(dialog);

    const nameInput = dialog.querySelector<HTMLInputElement>('.save-dialog__name')!;

    // Folder chips, filled in once the list arrives - the dialog opens straight
    // away rather than waiting on the network to show a name field.
    const folderBox = dialog.querySelector<HTMLElement>('.save-dialog__folders')!;
    const renderFolders = (names: string[]): void => {
      folderBox.innerHTML =
        `<button class="chip ${folder === null ? 'is-on' : ''}" data-folder="">${t('folder.none')}</button>` +
        names
          .map(
            (f) =>
              `<button class="chip ${folder === f ? 'is-on' : ''}" data-folder="${escapeAttr(f)}">${escapeAttr(f)}</button>`,
          )
          .join('') +
        `<button class="chip chip--new" data-folder-new="1">+ ${t('folder.new')}</button>`;
      folderBox.querySelectorAll<HTMLButtonElement>('[data-folder]').forEach((b) =>
        b.addEventListener('click', () => {
          folder = b.dataset.folder || null;
          renderFolders(names);
        }),
      );
      folderBox.querySelector<HTMLElement>('[data-folder-new]')?.addEventListener('click', () => {
        const made = prompt(t('folder.newPrompt'))?.trim();
        if (!made) return;
        folder = made;
        renderFolders(names.includes(made) ? names : [...names, made]);
      });
    };
    renderFolders([]);
    void authApi
      .fetchFolders()
      .then(({ folders }) => renderFolders(folders))
      .catch(() => {
        /* offline: "No folder" and "New folder" still work */
      });
    dialog.querySelectorAll<HTMLButtonElement>('[data-cat]').forEach((b) =>
      b.addEventListener('click', () => {
        selected = b.dataset.cat as authApi.PlaceCategory;
        dialog.querySelectorAll('[data-cat]').forEach((x) => x.classList.toggle('is-on', x === b));
        // Convenience: picking a category with the default name pre-fills it.
        if (!nameInput.value.trim() || nameInput.value === prefill) {
          const cat = categories.find((c) => c.id === selected);
          if (cat && selected !== 'other') nameInput.value = cat.label;
        }
      }),
    );
    dialog.querySelector('[data-act=cancel]')!.addEventListener('click', () => dialog.remove());
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.remove();
    });
    dialog.querySelector('[data-act=save]')!.addEventListener('click', () => {
      const name = nameInput.value.trim() || prefill;
      void authApi
        .createPlace(name, center[0], center[1], 'place', selected, folder)
        .then(async () => {
          dialog.remove();
          await loadPlaces();
          renderPreview();
        })
        .catch((err) => alert(err instanceof Error ? err.message : t('place.saveFailed')));
    });
  }

  const settingsPanel = new SettingsPanel(
    uiEl,
    () => currentUser,
    () => {
      setPanel(null);
      // The avoid-tolls / avoid-motorways toggles live here now, so closing
      // settings has to re-run the search the way the on-sheet chips used to.
      // Otherwise the preference is saved and the routes on screen ignore it.
      if (store.get().mode === 'preview') void refetchPreviewRoutes();
    },
    () => {
      void authApi.logout().finally(() => location.reload());
    },
  );

  const tripsPanel = new TripsPanel(
    uiEl,
    () => setPanel(null),
    (trip) => {
      setPanel(null);
      void pickDestination({
        id: `trip:${trip.id}`,
        name: trip.destinationName,
        address: '',
        center: trip.destination,
        kind: 'history',
      });
    },
    (trip) => {
      setPanel(null);
      showTripOnMap(trip);
    },
  );

  /*
   * A recorded trip, laid back on the map.
   *
   * The track is stored with time and speed per point precisely so questions
   * like "how fast did we go through there" stay answerable after the fact:
   * tap the drawn line and the popup shows the recorded clock time and speed
   * at that spot, with one tap more to navigate back to it. The floating chip
   * is the way out - without it the coloured line looks like a stuck overlay.
   */
  const tripViewChip = document.createElement('button');
  tripViewChip.className = 'trip-view-chip';
  tripViewChip.hidden = true;
  uiEl.appendChild(tripViewChip);
  tripViewChip.addEventListener('click', () => clearTripView());

  function clearTripView(): void {
    mapView.clearTripTrack();
    tripViewChip.hidden = true;
  }

  function showTripOnMap(trip: Trip): void {
    const points = (trip.track ?? []).map(readPoint).filter((p) => p[0] !== 0 || p[1] !== 0);
    if (points.length < 2) return;
    const units = store.get().settings.units;
    mapView.showTripTrack(points, {
      label: (p) => {
        const kmh = p[3];
        const shown = units === 'imperial' ? Math.round(kmh / 1.609344) : Math.round(kmh);
        const unit = units === 'imperial' ? 'mph' : 'km/h';
        return `<strong>${shown} ${unit}</strong> · ${formatClock(p[2])}<br><small>${formatDateTime(p[2])}</small>`;
      },
      navLabel: t('trips.goHere'),
      onNavigate: (at) => {
        clearTripView();
        void pickDroppedPin(at);
      },
    });
    tripViewChip.hidden = false;
    tripViewChip.innerHTML = `✕ <span>${escapeText(trip.destinationName)} · ${formatDateTime(trip.startedAt)}</span>`;
  }

  const spotifyBar = new SpotifyBar(uiEl, spotifyPlayer);

  /**
   * Re-centre control.
   *
   * Shown only while actually navigating, and only once the vehicle has
   * genuinely left the screen - not the instant the map is nudged. A button
   * that appears on every small pan is noise, and one that appears when there
   * is no route to return to is meaningless. The HUD owns the control itself;
   * this only decides when it is warranted.
   */
  function refreshRecenter(): void {
    const st = store.get();
    // Shown whenever the vehicle has left the screen - with or without a
    // route. Panning away from yourself while parked is just as disorienting
    // as doing it mid-drive, and it was the same button either way.
    if (!st.fix || st.mode === 'preview') {
      hud.setRecenterVisible(false);
      return;
    }
    const strayed = !mapView.isFollowing && mapView.vehicleOffScreen([st.fix.lng, st.fix.lat]);
    hud.setRecenterVisible(strayed);
  }
  mapView.map.on('moveend', refreshRecenter);

  /*
   * "Back to start".
   *
   * Every delivery is a there-and-back, and getting home meant searching for
   * an address the driver had just driven away from. The origin of the current
   * trip is remembered when navigation begins and offered as one tap once the
   * trip is over - which is exactly when it is wanted.
   */
  let tripOrigin: Place | null = null;

  const backButton = document.createElement('button');
  backButton.className = 'back-start';
  backButton.hidden = true;
  uiEl.appendChild(backButton);
  backButton.addEventListener('click', () => {
    if (tripOrigin) void pickDestination(tripOrigin);
  });

  /** Minimal escaping for the one place main.ts writes user text into HTML. */
  function escapeText(v: string): string {
    return v.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  function refreshBackButton(): void {
    const st = store.get();
    // Only in idle: mid-navigation it would compete with the instruction, and
    // in preview the driver is already choosing a destination.
    const show = !!tripOrigin && st.mode === 'idle';
    backButton.hidden = !show;
    if (!show) return;
    backButton.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M11 5 L4 12 L11 19 M4 12 H15 a5 5 0 0 1 5 5 V19" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span class="back-start__text">
        <strong>${t('nav.backToStart')}</strong>
        <em>${escapeText(tripOrigin!.name)}</em>
      </span>`;
  }

  const vehiclePicker = new VehiclePicker(uiEl, (id) => {
    if (id === store.get().settings.activeVehicleId) return;
    store.patchSettings({ activeVehicleId: id });
    // Dimensions feed straight into the routing request, so the routes on
    // screen are wrong the moment the vehicle changes.
    if (store.get().mode === 'preview') void refetchPreviewRoutes();
    else renderPreview();
  });

  /* --------------------------- fleet + messaging ------------------------ */

  const fleet = new Fleet({
    onDrivers: (contacts) => fleetBubble.update(contacts),
    onIncoming: (msg) => {
      fleetBubble.showIncoming(msg);
      // Spoken in THIS driver's language - the wire only carried an enum.
      announce(t('msg.incoming.voice', { name: msg.fromName, what: t(`msg.${msg.kind}`) }));
    },
    onReply: (msg, announceIt) => onMessageReply(msg, announceIt),
  });

  const fleetBubble = new FleetBubble(uiEl, {
    onSend: (to, kind) => void sendQuickMessage(to, kind),
    onReply: (id, reply) => void fleet.reply(id, reply).catch(() => {}),
  });
  fleetBubble.setUnits(store.get().settings.units);
  fleet.start();

  async function sendQuickMessage(to: number, kind: MessageKind): Promise<void> {
    const name = fleet.roster.find((c) => c.id === to)?.name ?? '';
    try {
      await fleet.send(to, kind);
      statusFlash(t('msg.sent', { name }));
      if (store.get().mode === 'navigating') hud.toast(t('msg.sent', { name }));
    } catch {
      statusFlash(t('msg.sendFailed'));
    }
  }

  /**
   * `announceIt` is false for answers adopted from the backlog on a cold start:
   * they still populate the roster, but an hour-old "Yes" must not be spoken or
   * flashed as though it had just come in.
   */
  function onMessageReply(msg: QuickMessage, announceIt = true): void {
    // Backlog adopted on a cold start: fill the roster, stay silent. An answer
    // from an hour ago must not put a card on screen demanding acknowledgement.
    if (!announceIt) {
      fleetBubble.noteReply(msg);
      return;
    }

    // The card holds until the driver taps OK - no toast to miss.
    fleetBubble.showReply(msg);
    const answer = t(msg.reply === 'yes' ? 'msg.yes' : 'msg.no');
    // The reply is about a question WE asked, so `fromName` is us; use the
    // roster to name the person who actually answered.
    const who = fleet.roster.find((c) => c.id === msg.to)?.name ?? '';
    announce(t('msg.reply.voice', { name: who, answer }), 'action');
  }

  /* ------------------------------ PTT stops ----------------------------- */

  const fuelBubble = new FuelBubble(uiEl, (stop) => void takeFuelDetour(stop));

  /* Live telemetry - everything being recorded, as it happens. */
  let liveSamples = 0;
  let liveRough: number | null = null;
  const livePanel = new LiveDataPanel(uiEl, () => {
    const st = store.get();
    const prog = st.progress;
    return {
      fix: st.fix,
      progress: st.mode === 'navigating' ? prog : null,
      roughness: liveRough,
      tier: prog ? tierForStep(prog.step) : null,
      tripDistance: recorder?.distance ?? 0,
      samples: liveSamples,
      movingSeconds: Math.round(liveMovingSeconds),
      gradeNow: gradeAtCurrentPosition(),
      signalsOnRoute: routeStats[st.selectedRouteIndex]?.trafficSignals ?? null,
      fleetCount: fleet.roster.length,
      units: st.settings.units,
    };
  });
  let liveMovingSeconds = 0;
  let lastFixAt = 0;

  /** Gradient under the wheels right now, from the loaded elevation profile. */
  function gradeAtCurrentPosition(): number | null {
    const prog = store.get().progress;
    if (!elevation || !prog) return null;
    const at = prog.snapped.distanceAlongRoute;
    const s1 = elevation.samples;
    let lo = 0;
    for (let i = 1; i < s1.length; i++) {
      if (s1[i][0] > at) break;
      lo = i;
    }
    const a = s1[lo];
    const b = s1[Math.min(s1.length - 1, lo + 2)];
    const run = b[0] - a[0];
    if (run <= 0) return null;
    return Math.round(((b[1] - a[1]) / run) * 1000) / 10;
  }

  /* Everything worth knowing about the journey, in one place. */
  const routeInfo = new RouteInfoSheet(uiEl, () => {
    const st = store.get();
    return {
      route: st.routes[st.selectedRouteIndex] ?? navigator_?.route ?? null,
      stats: routeStats[st.selectedRouteIndex] ?? routeStats[0] ?? null,
      elevation,
      progress: st.mode === 'navigating' ? st.progress : null,
      fuelStop: fuelStops[0] ?? null,
      units: st.settings.units,
      speeds: st.settings.speeds,
      vehicleName: activeVehicle(st.settings).name,
    };
  });
  let fuelStops: FuelStop[] = [];
  let nearestFuel: FuelStop | null = null;
  let allFuel: FuelStop[] = [];
  let fuelSearchedAt = -Infinity;
  let fuelSearchInFlight = false;
  /** The route we were on before a fuel detour, so Undo can put it back. */
  let routeBeforeDetour: Route | null = null;

  /**
   * Live refinement: ask about THIS junction when we are close, the rules have
   * produced nothing, and the prefetch did not cover it.
   *
   * The prefetch handles the ordinary case. This exists for the ones it cannot:
   * a reroute or fuel detour built a step list the prefetch never saw, or the
   * junction was there but the model declined it the first time and we are now
   * close enough that silence is expensive. Guarded three ways - only when
   * blank, only inside REFINE_AT_M, and deduped per index inside the cache - so
   * a junction costs at most one call however many fixes arrive on the approach.
   */
  const REFINE_AT_M = 1_200;
  function refineGuideIfBlank(progress: NavProgress): void {
    if (!laneGuides || !navigator_) return;
    const fork = progress.fork;
    // Nothing to fill: the rules already said something about this junction.
    if (fork?.lanes?.length || progress.lanes?.length || progress.laneAdvice) return;
    if (progress.aiGuide) return;

    const distance = fork ? fork.distance : progress.distanceToManeuver;
    if (distance > REFINE_AT_M) return;

    const steps = navigator_.route.legs.flatMap((l) => l.steps);
    const target = progress.maneuverStep;
    const idx = fork ? fork.stepIndex : target ? steps.indexOf(target) : -1;
    if (idx < 1 || idx >= steps.length) return;

    void laneGuides.refine(
      idx,
      steps[idx - 1] ?? null,
      steps[idx],
      getLang(),
      progress.drivingSide,
    );
  }

  async function refreshFuelStop(along: number): Promise<void> {
    const route = navigator_?.route;
    if (!route || fuelSearchInFlight) return;
    // Re-query only every 5 km, or once the chosen stop is behind us.
    const stale = fuelStops[0] ? along > fuelStops[0].along - 300 : false;
    if (!stale && Math.abs(along - fuelSearchedAt) < 5000) return;

    fuelSearchInFlight = true;
    fuelSearchedAt = along;
    try {
      const stops = await findFuelStopsAhead({
        route,
        along,
        drivingSide: store.get().progress?.drivingSide ?? 'left',
      });
      // Keep a couple: the sheet offers a second choice when the nearest is
      // served by a frontage road.
      fuelStops = stops.ptt.slice(0, 2);
      nearestFuel = stops.nearest;
      allFuel = stops.all;
    } catch {
      /* leave the previous stop in place rather than flickering the bubble */
    } finally {
      fuelSearchInFlight = false;
    }
  }

  async function takeFuelDetour(stop: FuelStop): Promise<void> {
    const dest = store.get().destination;
    const fix = store.get().fix;
    if (!dest || !fix || !navigator_) return;

    hud.toast(t('fuel.detour', { name: stop.name }), true);
    try {
      const routes = await fetchRoutes({
        origin: [fix.lng, fix.lat],
        destination: dest.center,
        // Routing THROUGH the station means the trip carries on to the real
        // destination by itself once we pull out - nothing to unwind.
        via: [stop.location],
        heading: fix.heading,
        settings: { ...store.get().settings, route: { ...store.get().settings.route, alternatives: false } },
      });
      const s2 = store.get().settings;
      const detour = applyPace(routes, s2.speeds, s2.signalSeconds)[0];
      routeBeforeDetour = navigator_.route;
      navigator_.replaceRoute(detour);
      routeRough = roughnessOfRoute(detour, roughIndex, store.get().settings.roughSensitivity);
      routeCumulative = cumulativeDistances(detour.geometry.coordinates);
      routeStats = [computeRouteStats(detour)];
      store.set({ routes: [detour], selectedRouteIndex: 0 });
      mapView.drawRoute(detour);
      fuelStops = [];
    nearestFuel = null;
    allFuel = [];
      fuelBubble.hide();
      hud.toast(t('fuel.detour', { name: stop.name }), false, {
        label: t('fuel.undo'),
        onAction: () => undoFuelDetour(),
      });
    } catch {
      hud.toast(t('fuel.failed'));
    }
  }

  function undoFuelDetour(): void {
    const previous = routeBeforeDetour;
    if (!previous || !navigator_) return;
    routeBeforeDetour = null;
    navigator_.replaceRoute(previous);
    routeCumulative = cumulativeDistances(previous.geometry.coordinates);
    routeStats = [computeRouteStats(previous)];
    store.set({ routes: [previous], selectedRouteIndex: 0 });
    mapView.drawRoute(previous);
    fuelSearchedAt = -Infinity;
    hud.toast(t('fuel.restored'));
  }

  idleBar.querySelector('[data-act=search]')!.addEventListener('click', () => {
    primeSpeech(); // user gesture unlocks TTS for later
    setPanel('search');
  });
  idleBar.querySelector('[data-act=settings]')!.addEventListener('click', () => setPanel('settings'));
  idleBar.querySelector('[data-act=trips]')!.addEventListener('click', () => setPanel('trips'));
  idleBar.querySelector('[data-act=share]')!.addEventListener('click', () => {
    const f = store.get().fix;
    if (!f) {
      // Indoors / basement / no signal: instead of dead-ending on "try again",
      // arm pin-share - the next long-press on the map saves THAT spot.
      pinShareArmed = true;
      statusFlash(t('idle.noFixPin'));
      return;
    }
    // A fix is available, so this share uses it - drop any stale armed state
    // from an earlier no-fix press, or the next long-press would hijack it.
    pinShareArmed = false;
    // Name it NOW, in one popup, rather than editing the auto-label later -
    // "ปั๊มยาง Williams" is findable next month; "Petteri · 14:32" is not.
    // The position is captured before the dialog, not after: the vehicle may
    // move while the name is being typed, and the place being saved is where
    // the button was pressed.
    const at: LngLat = [f.lng, f.lat];
    const now = new Date();
    const label = `${currentUser?.name ?? 'Driver'} · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    void openNameDialog(t('share.title'), label).then((name) => {
      if (name == null) return;
      void authApi
        .createPlace(name, at[0], at[1], 'shared-location')
        .then(async () => {
          await loadPlaces();
          statusFlash(t('idle.shared'));
        })
        .catch(() => statusFlash(t('idle.shareFailed')));
    });
  });

  let statusFlashTimer: number | null = null;
  function statusFlash(message: string): void {
    statusEl.textContent = message;
    statusEl.hidden = false;
    if (statusFlashTimer !== null) clearTimeout(statusFlashTimer);
    statusFlashTimer = window.setTimeout(() => {
      statusEl.hidden = true;
    }, 3500);
  }

  function setPanel(panel: 'search' | 'settings' | 'trips' | null): void {
    searchPanel.close();
    settingsPanel.close();
    tripsPanel.close();
    if (panel === 'search') searchPanel.open();
    if (panel === 'settings') void settingsPanel.open();
    if (panel === 'trips') void tripsPanel.open();
    store.set({ panel });
  }

  /* -------------------------------- GPS -------------------------------- */
  const statusEl = idleBar.querySelector('.idlebar__status') as HTMLElement;

  // The push worker renders notifications in this device's language and cannot
  // read localStorage, so keep its copy current from the moment we boot.
  void mirrorLangForWorker();

  trace('ui:ok');
  const gps = new GpsWatcher({
    onFix: (fix) => {
      const first = !store.get().located;
      store.set({ fix, located: true, gpsError: null });
      statusEl.hidden = true;
      if (first) mapView.jumpTo([fix.lng, fix.lat], 15.5);

      fleet.setFix(fix);
      if (breadcrumbs.add(fix)) redrawCrumbs();
      refreshRecenter();
      const mode = store.get().mode;
      if (mode === 'navigating') {
        onNavFix(fix);
      } else {
        mapView.updatePuck(fix);
      }
    },
    onError: (message) => {
      store.set({ gpsError: message });
      statusEl.textContent = message;
      statusEl.hidden = false;
    },
  });
  gps.start();

  function currentFixLngLat(): LngLat | null {
    const f = store.get().fix;
    return f ? [f.lng, f.lat] : null;
  }

  /* ------------------------------ routing ------------------------------ */
  let routeAbort: AbortController | null = null;
  /** Traffic-light / turn / toll counts for the current candidate routes. */
  let routeStats: RouteStats[] = [];
  let advice: RouteAdvice | null = null;
  /** Gradient profile of the selected route for the active vehicle. */
  let elevation: ElevationProfile | null = null;
  let adviceLoading = false;
  let adviceDismissed = false;
  let lastAdviceKey = '';
  let adviceAbort: AbortController | null = null;

  /**
   * Armed by the share button when there is no GPS fix: the next long-press
   * saves that spot for the fleet instead of opening a route preview, so a
   * place can still be shared from a basement or before the first fix.
   */
  let pinShareArmed = false;

  async function pickDroppedPin(point: LngLat): Promise<void> {
    if (store.get().mode === 'navigating') return;
    if (pinShareArmed) {
      pinShareArmed = false;
      const now = new Date();
      const label = `${currentUser?.name ?? 'Driver'} · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const name = await openNameDialog(t('share.title'), label);
      if (name == null) return;
      try {
        await authApi.createPlace(name, point[0], point[1], 'shared-location');
        await loadPlaces();
        statusFlash(t('idle.shared'));
      } catch {
        statusFlash(t('idle.shareFailed'));
      }
      return;
    }
    const place = await reverseGeocode(point);
    void pickDestination(place);
  }

  async function pickDestination(place: Place): Promise<void> {
    setPanel(null);
    backButton.hidden = true;
    advice = null;
    adviceDismissed = false;
    lastAdviceKey = '';
    store.set({ destination: place, mode: 'preview', routes: [], routeError: null });
    mapView.setDestination(place.center);
    void addRecent(place);
    await refetchPreviewRoutes();
  }

  async function refetchPreviewRoutes(): Promise<void> {
    const state = store.get();
    const dest = state.destination;
    const origin = plannedOrigin ? plannedOrigin.center : currentFixLngLat();
    if (!dest) return;
    if (!origin) {
      /*
       * No GPS, but the trip is still plannable.
       *
       * This used to sit on "Waiting for a GPS fix…" indefinitely, which is
       * the wrong answer for a driver working out tomorrow's run from a
       * basement, a workshop or anywhere else with no sky. The destination is
       * known and perfectly good; the only missing piece is where to start
       * FROM, and the sheet already has a row for exactly that. So say so, and
       * show the place on the map meanwhile - it is the thing being decided
       * about, and leaving the map wherever it happened to be made it look as
       * though the search had failed.
       */
      mapView.jumpTo(dest.center, 14.5);
      store.set({ routeError: t('preview.noFixSetStart') });
      preview.render({
        destination: dest,
        routes: [],
        stats: [],
        selectedIndex: 0,
        loading: false,
        error: t('preview.noFixSetStart'),
        isSaved: isDestSaved(),
        originName: plannedOrigin?.name ?? null,
        noFix: true,
      });
      return;
    }

    routeAbort?.abort();
    routeAbort = new AbortController();
    store.set({ routing: true, routeError: null });
    preview.render({
      destination: dest,
      routes: [],
      stats: [],
      selectedIndex: 0,
      loading: true,
      error: null,
    });

    try {
      // Corridors this driver has used before for the same destination, so a
      // route they know is better than the engine's gets offered explicitly.
      const past = await listTrips(80);
      const learned = learnedVias(past, origin, dest.center).map((c) => c.vias);

      const fetched = await fetchRouteAlternatives(
        {
          origin,
          destination: dest.center,
          heading: plannedOrigin ? null : (state.fix?.heading ?? null),
          settings: store.get().settings,
          signal: routeAbort.signal,
        },
        learned,
      );
      // Re-time and re-rank against the driver's own cruising speeds and the
      // cost of a red light, so the option list is ordered by how long the
      // journey will actually take THEM.
      const s = store.get().settings;
      let routes = applyPace(fetched, s.speeds, s.signalSeconds);

      /*
       * Let a bad surface actually cost a route its place.
       *
       * "the rough road should be a deterring thing for future routes, so it
       * has to be considered strongly." The penalty is applied to the RANKING
       * only - never to the ETA shown - because the drive does not take longer
       * for our disliking the road. See roughPenaltySeconds().
       */
      roughPerRoute = routes.map((r) => roughnessOfRoute(r, roughIndex, s.roughSensitivity));
      widthPerRoute = routes.map(roadWidthProfile);
      if (s.avoidRough && routes.length > 1) {
        const order = routes
          .map((r, i) => ({ r, i, cost: paceDuration(r) + roughPenaltySeconds(roughPerRoute[i]) }))
          .sort((a, b) => a.cost - b.cost);
        routes = order.map((o) => o.r);
        roughPerRoute = order.map((o) => roughPerRoute[o.i]);
        widthPerRoute = order.map((o) => widthPerRoute[o.i]);
      }

      routeStats = statsFor(routes);
      pttPerRoute = [];
      clearancePerRoute = [];
      store.set({ routes, selectedRouteIndex: 0, routing: false });
      routeRough = roughPerRoute[0] ?? null;
      elevation = null;
      renderPreview();
      requestAnimationFrame(fitPreview);
      routeInfo.setVisible(true);
      void refreshElevation();
      void requestAdvice();
      void refreshPttGaps(routes, routeAbort.signal);
      void refreshClearances(routes, routeAbort.signal);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Routing failed.';
      routeStats = [];
      store.set({ routing: false, routeError: message, routes: [] });
      renderPreview();
    }
  }

  /**
   * PTT spacing per option, off the critical path like the advisor: the list
   * renders immediately and the fuel rows fill in when the searches land.
   * Computed once per fetch - selecting between A/B/C re-renders from the
   * cached array and costs no further requests.
   */
  async function refreshPttGaps(routes: Route[], signal: AbortSignal): Promise<void> {
    // One route at a time - all three at once triples the request burst and
    // trips the same per-token rate limit the chunking in pttGapStats avoids.
    // Each route's row fills in as its own searches land.
    const stats: Array<PttGapStats | null> = routes.map(() => null);
    for (let i = 0; i < routes.length; i++) {
      stats[i] = await pttGapStats(routes[i], signal).catch(() => null);
      if (signal.aborted || store.get().routes !== routes) return; // stale fetch
      pttPerRoute = [...stats];
      renderPreview();
    }
  }

  /**
   * Posted height limits per option, for the truck only - a car fits under
   * everything the routing would ever offer it, and the lookup is a request
   * to a public server that should not be spent for nothing. Off the critical
   * path like the fuel rows: the sheet renders immediately and the clearance
   * lines fill in when the scan lands. A failed scan leaves the array empty,
   * which the sheet reads as "unknown" and says nothing - never "clear".
   */
  async function refreshClearances(routes: Route[], signal: AbortSignal): Promise<void> {
    const v = activeVehicle(store.get().settings);
    if (v.kind !== 'truck' || !v.height) return;
    const scanned = await scanRouteClearances(routes, v.height, signal).catch(() => null);
    if (!scanned || signal.aborted || store.get().routes !== routes) return;
    clearancePerRoute = scanned;
    renderPreview();
  }

  /**
   * Ask the advisor for a second opinion. Entirely best-effort and entirely
   * off the critical path - the driver can hit GO before it ever answers, and
   * a server with no API key configured simply never shows the card.
   */
  async function requestAdvice(): Promise<void> {
    const s = store.get();
    const dest = s.destination;
    if (!s.settings.aiAdvice) return;
    if (!dest || s.routes.length < 2 || adviceDismissed) return;

    const input = {
      destination: dest,
      routes: s.routes,
      stats: routeStats,
      trips: await listTrips(60),
      settings: s.settings,
      elevation,
    };
    const key = adviceKey(input);
    if (key === lastAdviceKey) return; // same question, already answered
    lastAdviceKey = key;

    adviceAbort?.abort();
    adviceAbort = new AbortController();
    adviceLoading = true;
    renderPreview();

    try {
      const result = await fetchAdvice(input, adviceAbort.signal);
      if (store.get().destination !== dest) return; // driver moved on
      advice = result;
    } catch {
      advice = null; // never surface advisor failures to a driver
    } finally {
      adviceLoading = false;
      renderPreview();
    }
  }

  /**
   * Frame the whole route while the selector is open.
   *
   * The fit only ran on the first fetch, so picking a different option left the
   * camera where it was and the new route ran off the edge of the screen -
   * which defeats the point of showing alternatives. Re-fitting on every render
   * keeps whatever is selected fully in view.
   */
  function fitPreview(): void {
    const s = store.get();
    const shown = s.routes.length ? s.routes : [];
    if (!shown.length) return;
    const sheet = previewSheetRect();
    mapView.fitRoute(shown, isPortrait(), sheet.h, sheet.w);
  }

  function renderPreview(): void {
    const s = store.get();
    if (!s.destination || s.mode !== 'preview') return;
    // Which sailing each option is planned around; null for ferry-free routes.
    const isTruck = activeVehicle(s.settings).kind === 'truck';
    const ferryPerRoute = s.routes.map((r) =>
      planFerry(r, paceDuration(r), Date.now(), isTruck),
    );
    preview.render({
      destination: s.destination,
      routes: s.routes,
      stats: routeStats,
      selectedIndex: s.selectedRouteIndex,
      loading: s.routing,
      error: s.routeError,
      isSaved: isDestSaved(),
      advice,
      adviceLoading,
      elevation,
      vehicleName: activeVehicle(s.settings).name,
      originName: plannedOrigin?.name ?? null,
      roughness: routeRough,
      roughPerRoute,
      widthPerRoute,
      ferryPerRoute,
      pttPerRoute,
      clearancePerRoute,
    });
    const selected = s.routes[s.selectedRouteIndex];
    mapView.drawRoute(selected ?? null);
    mapView.drawAlternatives(s.routes.filter((_, i) => i !== s.selectedRouteIndex));
    mapView.drawRouteLetters(s.routes, s.selectedRouteIndex);
    // Measured after the sheet has been laid out, so the padding is right.
    requestAnimationFrame(fitPreview);
  }

  function selectRoute(index: number): void {
    store.set({ selectedRouteIndex: index });
    const picked = store.get().routes[index];
    if (picked) {
      routeRough =
        roughPerRoute[store.get().selectedRouteIndex] ??
        roughnessOfRoute(picked, roughIndex, store.get().settings.roughSensitivity);
    }
    elevation = null;
    renderPreview();
    void refreshElevation();
  }

  /**
   * Work out how hard the selected route is to climb, for THIS vehicle.
   * Runs after the routes are already on screen: it costs a handful of terrain
   * tiles and must never delay the driver seeing their options.
   */
  async function refreshElevation(): Promise<void> {
    const s = store.get();
    const route = s.routes[s.selectedRouteIndex];
    if (!route) return;
    const forRoute = route._id;
    try {
      const profile = await analyseElevation(route, activeVehicle(s.settings));
      // The driver may have picked a different route while we were fetching.
      if (store.get().routes[store.get().selectedRouteIndex]?._id !== forRoute) return;
      elevation = profile;
      renderPreview();
      routeInfo.refresh();
      void requestAdvice();
    } catch {
      /* terrain unavailable - the route list simply shows no gradient note */
    }
  }

  function activeRoute(): Route | null {
    const s = store.get();
    return s.routes[s.selectedRouteIndex] ?? null;
  }

  function cancelPreview(): void {
    routeInfo.setVisible(false);
    plannedOrigin = null;
    searchTarget = 'destination';
    routeAbort?.abort();
    preview.hide();
    mapView.drawRoute(null);
    mapView.drawAlternatives([]);
    mapView.drawRouteLetters([], 0);
    mapView.setDestination(null);
    store.set({ mode: 'idle', destination: null, routes: [], routeError: null });
    const f = store.get().fix;
    if (f) mapView.jumpTo([f.lng, f.lat], 15.5);
  }

  /* ----------------------------- navigation ---------------------------- */
  let navigator_: RouteNavigator | null = null;
  /** AI junction guidance for the route being driven. See ai/laneAdvisor.ts. */
  let laneGuides: LaneGuideCache | null = null;
  let rerouteInFlight = false;
  let lastRerouteAt = 0;
  let lastRerouteAnnounceAt = 0;
  let recorder: TripRecorder | null = null;
  /** Where we last looked up the village name, so we don't ask every fix. */
  let localityAt: LngLat | null = null;
  let localityInFlight = false;

  /* Road-surface knowledge accumulated from our own drives. */
  const roughMeter = new RoughnessMeter();
  /* Vehicle-condition metrics from the same sensor; see nav/vehicleHealth.ts. */
  const healthMonitor = new VehicleHealthMonitor();

  /*
   * Dotted trail of the last 100 km driven. Fed from EVERY fix, not only while
   * navigating - "where have we been" doesn't care whether a destination was
   * set. Redraws are throttled: a dot every 30 m does not need a map update
   * per fix.
   */
  const breadcrumbs = new BreadcrumbTrail();
  let crumbsDrawnAt = 0;
  function redrawCrumbs(force = false): void {
    if (!store.get().settings.breadcrumbTrail) return;
    const now = Date.now();
    if (!force && now - crumbsDrawnAt < 15_000) return;
    crumbsDrawnAt = now;
    mapView.setBreadcrumbs(breadcrumbs.lines());
  }
  redrawCrumbs(true);
  select(
    (st) => st.settings.breadcrumbTrail,
    (on) => (on ? redrawCrumbs(true) : mapView.clearBreadcrumbs()),
    { immediate: false },
  );

  /*
   * Post-trip vehicle alert. The HUD is torn down by the time the verdict is
   * in, so this is its own banner: visible until dismissed, spoken once. It
   * only ever says "worth checking", because a phone in a cradle is a trend
   * detector, not a mechanic.
   */
  const healthBanner = document.createElement('button');
  healthBanner.className = 'health-banner';
  healthBanner.hidden = true;
  uiEl.appendChild(healthBanner);
  healthBanner.addEventListener('click', () => (healthBanner.hidden = true));
  function showHealthAlert(metric: 'wheel' | 'damper' | 'vib'): void {
    const text = t(`health.${metric}`);
    healthBanner.textContent = `⚠ ${text}`;
    healthBanner.hidden = false;
    announce(text, 'action');
  }
  let roughIndex: RoughIndex = { cells: new Map(), count: 0 };
  let routeRough: RouteRoughness | null = null;
  let roughPerRoute: Array<RouteRoughness | null> = [];
  let widthPerRoute: Array<RoadWidthProfile | null> = [];
  let pttPerRoute: Array<PttGapStats | null> = [];
  let clearancePerRoute: Array<RouteClearance | null> = [];
  let warnedRoughAt = -1;

  async function refreshRoughIndex(): Promise<void> {
    try {
      const trips = await listTrips(120);
      roughIndex = buildRoughIndex(trips);
      await refreshDrivenOverlay(trips);
    } catch {
      /* no history yet - the overlay and warnings simply stay quiet */
    }
  }

  /** Redraw the "roads we've driven" overlay in whichever mode is selected. */
  async function refreshDrivenOverlay(preloaded?: Awaited<ReturnType<typeof listTrips>>): Promise<void> {
    const mode = store.get().settings.drivenOverlay;
    if (mode === 'off') {
      mapView.clearDrivenTracks();
      return;
    }
    const trips = preloaded ?? (await listTrips(120));
    const tracks = trips.map((trip) =>
      (trip.track ?? [])
        .map((raw) => {
          const p = readPoint(raw);
          // Third value is whatever the overlay is colouring by.
          return [p[0], p[1], mode === 'rough' ? p[6] : p[3]] as [number, number, number];
        })
        // Thin long tracks: at overlay scale every third point is plenty.
        .filter((_, i) => i % 3 === 0),
    );
    mapView.setDrivenTracks(tracks, mode === 'rough' ? 'rough' : 'speed');
  }

  void refreshRoughIndex();
  select(
    (st) => st.settings.drivenOverlay,
    () => void refreshDrivenOverlay(),
    { immediate: false },
  );
  let routeCumulative: number[] = [];

  function startNavigation(): void {
    const route = activeRoute();
    const dest = store.get().destination;
    if (!route || !dest) return;

    // A route planned from somewhere else cannot be driven turn-by-turn from
    // here. Drop the planned origin and re-route from the vehicle's actual
    // position; the driver sees the corrected route immediately.
    const fixNow = currentFixLngLat();
    if (plannedOrigin && fixNow && distance(plannedOrigin.center, fixNow) > 250) {
      plannedOrigin = null;
      void refetchPreviewRoutes();
      return;
    }
    plannedOrigin = null;

    primeSpeech();
    // Motion access on iOS must be asked for from a user gesture; this is one.
    // The health monitor listens to the same devicemotion stream, so one grant
    // covers both.
    void RoughnessMeter.requestPermission().then((ok) => {
      if (ok) {
        roughMeter.start();
        healthMonitor.start(activeVehicle(store.get().settings).kind);
      }
    });
    // A history track left on the map would be indistinguishable from the
    // route at speed; reviewing ends when driving begins.
    clearTripView();
    preview.hide();
    mapView.drawAlternatives([]);
    mapView.drawRouteLetters([], 0);
    mapView.drawRoute(route);
    mapView.startNavigation();
    hud.show();
    appEl.dataset.mode = 'navigating';
    store.set({ mode: 'navigating' });
    routeInfo.setVisible(true);
    livePanel.setVisible(true);
    liveSamples = 0;
    liveMovingSeconds = 0;
    lastFixAt = 0;
    if (store.get().settings.keepAwake) enableWakeLock();

    routeCumulative = cumulativeDistances(route.geometry.coordinates);
    fleet.setDestinationName(dest.name);
    fuelStops = [];
    nearestFuel = null;
    allFuel = [];
    fuelSearchedAt = -Infinity;
    routeBeforeDetour = null;
    startTrafficWatch();

    // Where this trip began, so the driver can turn round and come back
    // without hunting for the address again.
    const startFix = store.get().fix;
    if (startFix) {
      tripOrigin = {
        id: `origin-${Date.now().toString(36)}`,
        // Placeholder until the reverse geocode below names it properly.
        name: t('nav.backToStart'),
        address: '',
        kind: 'place',
        center: [startFix.lng, startFix.lat] as LngLat,
      };
      void reverseGeocode([startFix.lng, startFix.lat])
        .then((place) => {
          // Keep the coordinates we actually started from; borrow only the name.
          const current: Place | null = tripOrigin;
          if (current && place.name) {
            tripOrigin = { ...current, name: place.name, address: place.address };
          }
          refreshBackButton();
        })
        .catch(() => {});
      refreshBackButton();
    }

    /*
     * AI junction guidance for this route.
     *
     * Prefetched in one call at departure so it is already in hand at speed,
     * with no latency at the junction and no dependence on signal there. A new
     * cache per route, never reused across a reroute: the indices belong to a
     * specific step list. Entirely best-effort - see ai/laneAdvisor.ts.
     */
    laneGuides = new LaneGuideCache();
    void laneGuides.prefetch(
      route.legs.flatMap((l) => l.steps),
      getLang(),
      route.legs[0]?.steps[0]?.driving_side ?? 'left',
    );

    navigator_ = new RouteNavigator(
      route,
      {
        // Turn instructions are the definition of "something needs doing", so
        // they still sound in alerts-only mode - as a chime.
        onAnnounce: (text: string) => announce(text, 'action'),
        onOffRoute: () => void handleOffRoute(),
        onArrive: () => endNavigation('arrived'),
      },
      // Announcements are composed by the navigator, so it needs to know which
      // language and units to compose them in.
      () => ({
        units: store.get().settings.units,
        lang: getLang(),
        guideFor: (i: number) => laneGuides?.get(i) ?? null,
      }),
    );

    const s = store.get().settings;
    if (s.logTrips) {
      const f = store.get().fix;
      const stats = routeStats[store.get().selectedRouteIndex] ?? computeRouteStats(route);
      recorder = new TripRecorder({
        destinationName: dest.name,
        destination: dest.center,
        originName: 'Current location',
        origin: f ? [f.lng, f.lat] : null,
        vehicle: activeVehicle(s),
        predictedDuration: route.duration,
        predictedDistance: route.distance,
        trafficSignals: stats.trafficSignals,
      });
    }

    announce(t('nav.voice.starting', { name: dest.name }), 'action');
  }

  function onNavFix(fix: Fix): void {
    if (!navigator_) return;
    const active = navigator_;
    const progress = active.update(fix);

    /*
     * `update()` fires onArrive synchronously, and onArrive tears the whole
     * navigation down. Everything below this line would then run against a
     * session that no longer exists: re-showing the maneuver card, writing
     * into a hidden HUD, restarting the camera - leaving the app in idle mode
     * with a half-live navigation UI and no way back short of restarting it.
     * That is the "bottom card disappears when we get to our destination"
     * report. If teardown happened, we are done.
     */
    if (navigator_ !== active) return;

    store.set({ progress });

    const s = store.get().settings;
    maneuverCard.update(progress, s.units, fix.speed ?? 0);
    refineGuideIfBlank(progress);
    if (s.adaptiveCamera || mapView.isFollowing) mapView.driveCamera(fix, progress);
    else mapView.updatePuck(fix, progress.snapped.location);

    // Overspeed check against the posted limit for the current segment.
    let overspeed = false;
    if (s.speedWarnOffset > 0 && fix.speed != null) {
      const limit = speedLimitAt(progress.snapped.distanceAlongRoute);
      if (limit != null) {
        overspeed = fix.speed * 3.6 > limit + s.speedWarnOffset;
      }
    }
    hud.update(progress, fix.speed, s.units, overspeed);
    routeInfo.refresh();

    // Feed the speed BEFORE reading, or the reading is judged against the
    // previous fix - which is how a reading survived the moment of stopping.
    roughMeter.setSpeed((fix.speed ?? 0) * 3.6);
    healthMonitor.setSpeed((fix.speed ?? 0) * 3.6);
    liveRough = roughMeter.read();

    // Only count samples the vehicle actually earned. Counting every fix meant
    // the surface counter climbed steadily while parked at a red light, which
    // is neither a distance driven nor a measurement of any road.
    if (liveRough != null) liveSamples++;
    if (lastFixAt && (fix.speed ?? 0) > 1) {
      liveMovingSeconds += Math.min(30, (fix.timestamp - lastFixAt) / 1000);
    }
    lastFixAt = fix.timestamp;
    livePanel.refresh();

    // Live warning before a stretch we already know is badly broken.
    if (routeRough?.known) {
      const ahead = roughAhead(routeRough, progress.snapped.distanceAlongRoute, 900);
      if (ahead && ahead.from !== warnedRoughAt) {
        warnedRoughAt = ahead.from;
        const away = Math.max(0, ahead.from - progress.snapped.distanceAlongRoute);
        hud.toast(t('rough.aheadToast', { d: formatDistanceShort(away, s.units) }));
        announce(t('rough.aheadVoice'), 'action');
      }
    }

    // Village / sub-district, so an unnamed road still says where you are.
    // Refreshed every ~2 km rather than per fix.
    if (!localityInFlight && (!localityAt || distance(localityAt, [fix.lng, fix.lat]) > 2000)) {
      localityInFlight = true;
      localityAt = [fix.lng, fix.lat];
      void reverseLocality([fix.lng, fix.lat])
        .then((name) => maneuverCard.setLocality(name))
        .catch(() => {})
        .finally(() => {
          localityInFlight = false;
        });
    }

    // Next PTT along the route, and how far to it.
    void refreshFuelStop(progress.snapped.distanceAlongRoute);
    if ((fuelStops.length || nearestFuel || allFuel.length) && navigator_) {
      const at = progress.snapped.distanceAlongRoute;
      const route = navigator_.route;
      const toChoice = (stop: FuelStop): FuelChoice => ({
        stop,
        remaining: Math.max(0, stop.along - at),
        eta: etaToStop(route, stop, at),
      });
      fuelBubble.update(
        fuelStops.map(toChoice),
        nearestFuel ? toChoice(nearestFuel) : null,
        allFuel.map(toChoice),
        s.units,
      );
    } else {
      fuelBubble.hide();
    }

    // Trip log: the recorder handles its own sampling cadence and rolls the
    // drive up into segments as it goes.
    recorder?.add(fix, roadLabel(progress.step, getLang()), tierForStep(progress.step), roughMeter.read());
  }

  function speedLimitAt(distanceAlong: number): number | null {
    const route = navigator_?.route;
    const max = route?.legs[0]?.annotation?.maxspeed;
    if (!max?.length || routeCumulative.length < 2) return null;
    // Binary search the geometry vertex for this distance.
    let lo = 0;
    let hi = routeCumulative.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (routeCumulative[mid] < distanceAlong) lo = mid + 1;
      else hi = mid;
    }
    const seg = Math.max(0, lo - 1);
    const entry = max[Math.min(seg, max.length - 1)];
    if (!entry || entry.unknown || entry.none || entry.speed == null) return null;
    return entry.unit === 'mph' ? entry.speed * 1.609344 : entry.speed;
  }

  async function handleOffRoute(): Promise<void> {
    if (rerouteInFlight || !navigator_) return;
    // Cooldown: repeated triggers (bad GPS patches) must not hammer the
    // routing API or chant "route updated" at the driver.
    if (Date.now() - lastRerouteAt < 20_000) return;
    const dest = store.get().destination;
    const fix = store.get().fix;
    if (!dest || !fix) return;

    lastRerouteAt = Date.now();
    rerouteInFlight = true;
    store.set({ rerouting: true });
    hud.toast(t('nav.rerouting'), true);

    try {
      const routes = await fetchRoutes({
        origin: [fix.lng, fix.lat],
        destination: dest.center,
        heading: fix.heading,
        settings: { ...store.get().settings, route: { ...store.get().settings.route, alternatives: false } },
      });
      const rs = store.get().settings;
      const route = applyPace(routes, rs.speeds, rs.signalSeconds)[0];
      navigator_.replaceRoute(route);
      routeCumulative = cumulativeDistances(route.geometry.coordinates);
      routeStats = [computeRouteStats(route)];
      recorder?.noteReroute();
      store.set({ routes: [route], selectedRouteIndex: 0 });
      mapView.drawRoute(route);
      hud.clearToast();
      if (Date.now() - lastRerouteAnnounceAt > 45_000) {
        lastRerouteAnnounceAt = Date.now();
        announce(t('nav.voice.updated'));
      }
    } catch {
      hud.toast(store.get().online ? t('nav.rerouteFailed') : t('nav.rerouteOffline'));
    } finally {
      rerouteInFlight = false;
      store.set({ rerouting: false });
    }
  }

  /* --------------------------- traffic watch ---------------------------- */
  /*
   * Every few minutes of navigation, quietly re-ask the router from the live
   * position. When real congestion sits ahead on the driven route AND a
   * fairly-easy alternative saves real time, offer the switch as a one-tap
   * toast - never reroute automatically. Decision rules in nav/trafficWatch.
   */
  let trafficTimer: number | null = null;
  let lastTrafficPromptAt = 0;
  let trafficCheckBusy = false;

  function startTrafficWatch(): void {
    stopTrafficWatch();
    lastTrafficPromptAt = 0;
    trafficTimer = window.setInterval(() => void checkTrafficAhead(), 240_000);
  }

  function stopTrafficWatch(): void {
    if (trafficTimer !== null) clearInterval(trafficTimer);
    trafficTimer = null;
  }

  async function checkTrafficAhead(): Promise<void> {
    const st = store.get();
    if (st.mode !== 'navigating' || !st.settings.trafficWatch || !st.online) return;
    if (!navigator_ || rerouteInFlight || trafficCheckBusy) return;
    const fix = st.fix;
    const dest = st.destination;
    const progress = st.progress;
    if (!fix || !dest || !progress) return;
    // Close to arrival there is nothing worth switching for; and don't nag -
    // one offer per ten minutes, taken or not.
    if (progress.distanceRemaining < 8_000) return;
    if (Date.now() - lastTrafficPromptAt < 10 * 60_000) return;

    trafficCheckBusy = true;
    try {
      const fetched = await fetchRouteAlternatives({
        origin: [fix.lng, fix.lat],
        destination: dest.center,
        heading: fix.heading,
        settings: { ...st.settings, route: { ...st.settings.route, alternatives: true } },
      });
      const s2 = store.get();
      if (s2.mode !== 'navigating' || !navigator_) return;
      const fresh = applyPace(fetched, s2.settings.speeds, s2.settings.signalSeconds);

      const samples = sampleRemaining(
        navigator_.route.geometry.coordinates,
        routeCumulative,
        s2.progress?.snapped.distanceAlongRoute ?? 0,
      );
      const advice = adviseOnTraffic(fresh, samples, computeRouteStats);
      if (!advice) return;

      lastTrafficPromptAt = Date.now();
      const min = Math.max(1, Math.round(advice.savesSeconds / 60));
      const alt = advice.alternative;
      announce(t('traffic.voice', { min }), 'action');
      hud.toast(t('traffic.ahead', { min }), true, {
        label: t('traffic.switch'),
        onAction: () => switchToRoute(alt),
      });
    } catch {
      /* offline blip or router error - the next tick simply tries again */
    } finally {
      trafficCheckBusy = false;
    }
  }

  /** Apply an accepted traffic-avoidance route mid-navigation. */
  function switchToRoute(route: Route): void {
    if (!navigator_ || store.get().mode !== 'navigating') return;
    navigator_.replaceRoute(route);
    routeCumulative = cumulativeDistances(route.geometry.coordinates);
    routeStats = [computeRouteStats(route)];
    routeRough = roughnessOfRoute(route, roughIndex, store.get().settings.roughSensitivity);
    recorder?.noteReroute();
    routeBeforeDetour = null;
    fuelStops = [];
    nearestFuel = null;
    allFuel = [];
    fuelSearchedAt = -Infinity;
    store.set({ routes: [route], selectedRouteIndex: 0 });
    mapView.drawRoute(route);
    hud.clearToast();
    hud.toast(t('traffic.switched'));
    announce(t('nav.voice.updated'));
  }

  function endNavigation(reason: 'arrived' | 'ended'): void {
    const dest = store.get().destination;
    if (reason === 'arrived' && dest) {
      announce(t('nav.voice.arrived', { name: dest.name }), 'action');
      hud.toast(t('nav.arrivedToast'));
    } else {
      stopSpeaking();
    }

    if (recorder) {
      // Never let a failure here strand the UI mid-teardown - the rest of this
      // function is what returns the app to a usable state.
      try {
        const done = recorder.finish(activeVehicle(store.get().settings));
        if (done) {
          // Attach what the accelerometer said about the VEHICLE this drive,
          // then judge it against this vehicle's own history - only once the
          // trip is saved, so the trend includes today.
          const health = healthMonitor.finish();
          if (health) done.health = health;
          void saveTrip(done).then(async () => {
            if (!health) return;
            const drift = assessHealthDrift(await listTrips(60), done.vehicleId);
            if (drift) showHealthAlert(drift);
          });
          void authApi.syncTrip(done).catch(() => {});
        }
      } catch (err) {
        console.warn('[trip] could not finish recording', err);
      }
      recorder = null;
    }

    navigator_ = null;
    stopTrafficWatch();
    roughMeter.stop();
    healthMonitor.stop();
    routeRough = null;
    warnedRoughAt = -1;
    void refreshRoughIndex();
    disableWakeLock();
    maneuverCard.hide();
    fuelBubble.hide();
    fuelStops = [];
    nearestFuel = null;
    allFuel = [];
    routeBeforeDetour = null;
    fleet.setDestinationName(null);
    hud.hide();
    mapView.stopNavigation();
    mapView.drawRoute(null);
    mapView.setDestination(null);
    appEl.dataset.mode = 'idle';
    routeInfo.setVisible(false);
    livePanel.setVisible(false);
    store.set({ mode: 'idle', destination: null, routes: [], progress: null });
    refreshBackButton();
    refreshRecenter();
    const f = store.get().fix;
    if (f) mapView.jumpTo([f.lng, f.lat], 15.5);
  }

  // ETA formatting reads this, so keep it in step with the setting.
  setClock24(store.get().settings.clock24);
  store.subscribe(() => setClock24(store.get().settings.clock24));

  /* ------------------------------ version tag --------------------------- */
  const versionEl = document.createElement('div');
  versionEl.className = 'version-tag';
  versionEl.textContent = `v${APP_VERSION}`;
  uiEl.appendChild(versionEl);

  /* ------------------------- viewport plumbing -------------------------- */
  /** Measured size of the preview sheet, so the route is fitted clear of it. */
  function previewSheetRect(): { w: number; h: number } {
    if (preview.el.hidden) return { w: 0, h: 0 };
    const r = preview.el.getBoundingClientRect();
    return { w: Math.round(r.width), h: Math.round(r.height) };
  }

  function isPortrait(): boolean {
    return window.innerHeight >= window.innerWidth;
  }
  window.addEventListener('resize', () => mapView.resize());
  window.addEventListener('orientationchange', () => setTimeout(() => mapView.resize(), 250));

  trace('done');
  // UI exists from here on - later async errors must not blank the screen.
  traceDone();
}

void gate();
